import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { AutoFixHigh, DeleteSweep, PlayArrow, Refresh } from '@mui/icons-material'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { popularityApi } from '@api/endpoints/popularity'
import type { PopularityItemOptionDto, SyntheticGenerateRequest, SyntheticItemDto, SyntheticRemoveRequest } from '@api/types/popularity'
import { apiErrorMessage, useAdminJobs, usePopularityStatus, useRefreshJobViews, useSyntheticItems } from '../hooks/useAdminJobs'
import {
  DEFAULT_SYNTHETIC_FORM,
  MAX_BACKFILL_DAYS,
  buildRecomputeRequest,
  buildSyntheticRequest,
  hourLabel,
  type SyntheticForm,
} from '../popularTimes'
import { JobRunStatus } from './JobRunStatus'
import { PopularityItemPicker } from './PopularityItemPicker'
import { PopularitySettingsEditor } from './PopularitySettingsEditor'

const POPULARITY_GROUP = 'popularity'

const when = (s?: string | null) => {
  if (!s) return 'Never'
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? 'Never' : d.toLocaleString()
}
const day = (s: string) => new Date(s).toLocaleDateString()
const HOURS = Array.from({ length: 25 }, (_, h) => h)

type Pending =
  | { kind: 'recompute'; request: object; summary: string }
  | { kind: 'generate'; request: SyntheticGenerateRequest; summary: string }
  | { kind: 'remove'; request: SyntheticRemoveRequest; summary: string }

/**
 * Statistics → Popular times (Backlog 062): pass status and row counts, an on-demand recompute (backfill up to
 * 120 days), the synthetic sample data (list, remove, generate) and every setting. Writes run as background jobs
 * through the Admin API job runner, one popular-times job at a time, and land in Job history.
 */
export function PopularTimesTab({ onSettingsDirtyChange }: {
  /** Passed through to the settings editor: true while it holds unsaved edits. */
  onSettingsDirtyChange?: (dirty: boolean) => void
} = {}) {
  const status = usePopularityStatus()
  const synthetic = useSyntheticItems()
  const jobs = useAdminJobs()
  const refresh = useRefreshJobViews()
  const busyJob = (jobs.data ?? []).find((j) => j.lockGroup === POPULARITY_GROUP && j.isRunning)

  // recompute
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [typical, setTypical] = useState(true)
  const [scores, setScores] = useState(true)
  const [recomputeError, setRecomputeError] = useState<string | null>(null)

  // synthetic
  const [form, setForm] = useState<SyntheticForm>(DEFAULT_SYNTHETIC_FORM)
  const [picked, setPicked] = useState<PopularityItemOptionDto | null>(null)
  const [formErrors, setFormErrors] = useState<string[]>([])

  const [pending, setPending] = useState<Pending | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [launch, setLaunch] = useState<{ jobId: string; title: string } | null>(null)

  const set = (patch: Partial<SyntheticForm>) => setForm((f) => ({ ...f, ...patch }))

  const askRecompute = () => {
    setRecomputeError(null)
    const built = buildRecomputeRequest(from, to, typical, scores)
    if (built.error || !built.request) return setRecomputeError(built.error ?? 'Invalid range.')
    const range = from ? `${from} to ${to || 'now'} (UTC)` : 'the usual recent window'
    const extra = [typical && 'typical weeks', scores && 'popularity scores'].filter(Boolean).join(' and ')
    setPending({ kind: 'recompute', request: built.request, summary: `Rebuild the real hourly rows for ${range}${extra ? `, then ${extra}` : ''}.` })
  }

  const askGenerate = () => {
    const built = buildSyntheticRequest(form)
    setFormErrors(built.errors)
    if (!built.request) return
    const r = built.request
    const caps = [r.weekdayCap != null && `weekdays at most ${r.weekdayCap}`, r.weekendCap != null && `weekends at most ${r.weekendCap}`].filter(Boolean).join(', ')
    const peak = r.busiestStartHour != null && r.busiestEndHour != null ? `, busiest ${hourLabel(r.busiestStartHour)} to ${hourLabel(r.busiestEndHour)}` : ''
    setPending({
      kind: 'generate',
      request: r,
      summary: `Replace the sample data of "${picked?.title ?? r.itemId}" with ${r.weeks} weeks peaking around ${r.peak} people${caps ? ` (${caps})` : ''}${peak}, then rebuild typical weeks.`,
    })
  }

  const askRemove = (item?: SyntheticItemDto) => setPending(item
    ? { kind: 'remove', request: { items: [{ itemType: item.itemType, itemId: item.itemId }] }, summary: `Delete the ${item.rows.toLocaleString()} synthetic hourly rows of "${item.title ?? item.itemId}", then rebuild typical weeks. Real data is not touched.` }
    : { kind: 'remove', request: { all: true }, summary: 'Delete every synthetic hourly row of every item, then rebuild typical weeks. Real data is not touched.' })

  const confirm = async () => {
    if (!pending) return
    setStarting(true)
    setStartError(null)
    try {
      const started = pending.kind === 'recompute'
        ? await popularityApi.recompute(pending.request as Parameters<typeof popularityApi.recompute>[0])
        : pending.kind === 'generate'
          ? await popularityApi.generateSynthetic(pending.request)
          : await popularityApi.removeSynthetic(pending.request)
      setLaunch({ jobId: started.jobId, title: pending.kind === 'recompute' ? 'Recompute' : pending.kind === 'generate' ? 'Generate sample data' : 'Remove sample data' })
      setPending(null)
      refresh()
    } catch (e) {
      setStartError(apiErrorMessage(e, 'The job could not be started.'))
    } finally {
      setStarting(false)
    }
  }

  const s = status.data
  const tiles = s ? [
    { label: 'Last hourly pass', value: when(s.lastHourlyComputedAt), sub: `every ${s.jobIntervalMinutes} min (User API)` },
    { label: 'Last typical weeks', value: when(s.lastTypicalComputedAt), sub: `nightly after ${s.nightlyUtcHour}:00 UTC` },
    { label: 'Last scores', value: when(s.lastScoresComputedAt), sub: `nightly after ${s.nightlyUtcHour}:00 UTC` },
    { label: 'Hourly rows', value: s.hourlyRowsReal.toLocaleString(), sub: `${s.hourlyRowsSynthetic.toLocaleString()} synthetic (${s.syntheticItems} items)` },
    { label: 'Typical slots', value: s.typicalSlots.toLocaleString(), sub: `${s.typicalItems.toLocaleString()} items` },
    { label: 'Score rows', value: s.scoreRows.toLocaleString(), sub: '7 and 30 days' },
    { label: 'View events', value: s.viewEvents.toLocaleString(), sub: 'raw, pruned by retention' },
  ] : []

  const syntheticColumns: DataTableColumn<SyntheticItemDto>[] = useMemo(() => [
    {
      key: 'title',
      header: 'Item',
      primary: true,
      render: (i) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{i.title ?? 'Deleted item'}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>{i.itemId}</Typography>
        </Box>
      ),
    },
    { key: 'type', header: 'Type', width: 100, render: (i) => <Chip size="small" variant="outlined" label={i.itemType === 'story' ? 'Story' : 'Adventure'} /> },
    { key: 'rows', header: 'Rows', align: 'right', width: 90, render: (i) => i.rows.toLocaleString() },
    { key: 'range', header: 'Hours', hideBelow: 'md', render: (i) => `${day(i.from)} to ${day(i.to)}` },
    { key: 'peak', header: 'Peak people', align: 'right', width: 110, hideBelow: 'md', render: (i) => i.peakPeople },
  ], [])

  const busy = !!busyJob

  return (
    <Stack spacing={3}>
      {launch && <JobRunStatus key={launch.jobId} jobId={launch.jobId} title={launch.title} onClose={() => setLaunch(null)} />}
      {busyJob && !launch && <Alert severity="info">{busyJob.name} is running. Other popular-times jobs wait until it finishes.</Alert>}

      {/* Status */}
      <Box>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Status</Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refresh()} aria-label="Refresh status"><Refresh fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>
        {status.error && <Alert severity="error">Could not load the status: {apiErrorMessage(status.error)}</Alert>}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          {tiles.map((t) => (
            <Card key={t.label} variant="outlined">
              <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{t.value}</Typography>
                <Typography variant="caption" color="text.secondary">{t.sub}</Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>

      {/* Recompute */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Recompute</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rebuilds the real hourly rows (empty dates: the usual recent window; or a backfill of up to {MAX_BACKFILL_DAYS} UTC days), then the typical weeks and scores if ticked. Synthetic rows are never touched.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} useFlexGap alignItems={{ sm: 'center' }} sx={{ flexWrap: 'wrap' }}>
          <TextField type="date" size="small" label="From (UTC)" value={from} onChange={(e) => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
          <TextField type="date" size="small" label="To (UTC, inclusive)" value={to} onChange={(e) => setTo(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: { xs: '100%', sm: 'auto' } }} />
          <FormGroup row>
            <FormControlLabel control={<Checkbox size="small" checked={typical} onChange={(e) => setTypical(e.target.checked)} />} label="Typical weeks" />
            <FormControlLabel control={<Checkbox size="small" checked={scores} onChange={(e) => setScores(e.target.checked)} />} label="Scores" />
          </FormGroup>
          <Button variant="contained" startIcon={<PlayArrow />} onClick={askRecompute} disabled={busy}>Recompute</Button>
        </Stack>
        {recomputeError && <Alert severity="error" sx={{ mt: 2 }}>{recomputeError}</Alert>}
      </Paper>

      {/* Synthetic data */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Synthetic sample data</Typography>
            <Typography variant="body2" color="text.secondary">
              Seeded hourly rows (IsSynthetic) shown in the chart beside real data. They never count toward popularity scores or trending.
            </Typography>
          </Box>
          <Button
            color="error"
            startIcon={<DeleteSweep />}
            onClick={() => askRemove()}
            disabled={busy || !(synthetic.data?.length)}
            sx={{ alignSelf: { sm: 'flex-start' }, flexShrink: 0 }}
          >
            Remove all
          </Button>
        </Stack>
        <DataTable
          rows={synthetic.data ?? []}
          columns={syntheticColumns}
          getRowKey={(i) => `${i.itemType}:${i.itemId}`}
          loading={synthetic.isLoading}
          error={synthetic.error ? apiErrorMessage(synthetic.error) : null}
          emptyMessage="No item has synthetic data."
          page={0}
          pageSize={100}
          pageSizeOptions={[100]}
          onPageChange={() => undefined}
          onPageSizeChange={() => undefined}
          rowActions={(i) => (
            <Button size="small" color="error" onClick={() => askRemove(i)} disabled={busy}>Remove</Button>
          )}
        />

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Generate sample data</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Same shape as tools/popularity/seed_popularity.py: weekday humps late morning and late afternoon, a broad weekend midday peak, views leading people by about 1.5 hours, ±20% noise. Re-running replaces the item's sample data. Caps limit the people present in any one hour; a busiest-hours window replaces the humps with one plateau.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2 }}>
          <TextField
            select size="small" label="Type" value={form.itemType}
            onChange={(e) => { set({ itemType: e.target.value as SyntheticForm['itemType'], itemId: '' }); setPicked(null) }}
          >
            <MenuItem value="adventure">Adventure</MenuItem>
            <MenuItem value="story">Story</MenuItem>
          </TextField>
          <Box sx={{ gridColumn: { sm: 'span 1', md: 'span 3' } }}>
            <PopularityItemPicker
              itemType={form.itemType}
              value={picked}
              onChange={(v) => { setPicked(v); set({ itemId: v?.itemId ?? '' }) }}
            />
          </Box>
          <TextField size="small" type="number" label="Weeks" value={form.weeks} onChange={(e) => set({ weeks: e.target.value })} inputProps={{ min: 1, max: 26 }} helperText="1 to 26, up to the last full hour" />
          <TextField size="small" type="number" label="Peak people" value={form.peak} onChange={(e) => set({ peak: e.target.value })} inputProps={{ min: 0.5, max: 500, step: 0.5 }} helperText="About how many at the busiest hour" />
          <TextField size="small" type="number" label="Weekday cap" value={form.weekdayCap} onChange={(e) => set({ weekdayCap: e.target.value })} inputProps={{ min: 0, max: 500 }} helperText="Most people Mon to Fri (blank: none)" />
          <TextField size="small" type="number" label="Weekend cap" value={form.weekendCap} onChange={(e) => set({ weekendCap: e.target.value })} inputProps={{ min: 0, max: 500 }} helperText="Most people Sat and Sun (blank: none)" />
          <TextField select size="small" label="Busiest from" value={form.busiestStartHour} onChange={(e) => set({ busiestStartHour: e.target.value })} helperText="Local time (blank: built-in curve)">
            <MenuItem value="">Built-in curve</MenuItem>
            {HOURS.slice(0, 24).map((h) => <MenuItem key={h} value={String(h)}>{hourLabel(h)}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Busiest until" value={form.busiestEndHour} onChange={(e) => set({ busiestEndHour: e.target.value })} helperText="Exclusive">
            <MenuItem value="">Built-in curve</MenuItem>
            {HOURS.slice(1).map((h) => <MenuItem key={h} value={String(h)}>{hourLabel(h)}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Time zone" value={form.timeZone} onChange={(e) => set({ timeZone: e.target.value })} placeholder="America/Chicago" helperText="Blank: from the item's location" />
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Button variant="contained" startIcon={<AutoFixHigh />} onClick={askGenerate} disabled={busy} fullWidth>Generate</Button>
          </Box>
        </Box>
        {formErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {formErrors.map((e) => <div key={e}>{e}</div>)}
          </Alert>
        )}
      </Paper>

      {/* Settings */}
      <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Saving recomputes nothing: the hourly table follows within one job interval, typical weeks and scores at the nightly pass, or run a recompute above.
        </Typography>
        <PopularitySettingsEditor onDirtyChange={onSettingsDirtyChange} />
      </Paper>

      <ConfirmDialog
        open={!!pending}
        title={pending?.kind === 'recompute' ? 'Recompute popular times?' : pending?.kind === 'generate' ? 'Generate sample data?' : 'Remove sample data?'}
        message={pending?.summary}
        confirmLabel={pending?.kind === 'remove' ? 'Remove' : pending?.kind === 'generate' ? 'Generate' : 'Recompute'}
        destructive={pending?.kind === 'remove'}
        loading={starting}
        onConfirm={confirm}
        onClose={() => { setPending(null); setStartError(null) }}
      >
        {startError && <Alert severity="error" sx={{ mt: 2 }}>{startError}</Alert>}
      </ConfirmDialog>
    </Stack>
  )
}
